import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";
import Modal from "@/components/controls/Modal";
import SingleSelect from "@/components/controls/SingleSelect";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import {
  HubSpotFormConfig,
  LeadOwnerEmails,
  ProductInterestOrigin
} from "@/entities/hubspot/types";
import { CampaignType, LeadType } from "@/pages/leads/types";
import { getCampaignTypeOptions } from "@/pages/leads/components/LeadDetailFields/utils";
import {
  HUBSPOT_SETTINGS_TRANSLATION_PREFIX,
  LEAD_SOURCES,
  MAX_NAME_LENGTH,
  MAX_NOTE_PROPERTIES,
  MAX_OWNER_RULES,
  MAX_VALUE_LABELS,
  blankToNull,
  formConfigErrors,
  formatProducts,
  parseProducts,
  valueLabelsFromRows
} from "@/pages/settings/components/integrations/hubspot/hubspotSettingsOptions";
import { OwnerSelect } from "@/pages/settings/components/integrations/hubspot/OwnerSelect";

const PREFIX = `${HUBSPOT_SETTINGS_TRANSLATION_PREFIX}.form`;
const ORIGINS: ProductInterestOrigin[] = ["none", "field", "fixed"];
const OWNER_ROLES: (keyof LeadOwnerEmails)[] = ["sdrOwner", "aeOwner"];

const SELECT_CLASSNAME = "text-start border border-border-border2 pl-0";
const SELECT_TRIGGER_CLASSNAME = "w-full min-h-8 h-9";

interface HubSpotFormDialogProps {
  config: HubSpotFormConfig;
  campaignTypes: CampaignType[];
  isLoadingCampaignTypes: boolean;
  isSaving: boolean;
  // The backend refuses a product interest until the page-level property is set.
  hasProductInterestProperty: boolean;
  onSave: (config: HubSpotFormConfig) => void;
  onClose: () => void;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children
}) => (
  <div className="flex flex-col gap-1">
    <Text variant="bodySecondary">{label}</Text>
    {children}
  </div>
);

// Mounted per open, so the draft always starts from the saved form. A field
// the admin leaves untouched goes back exactly as it was read.
export const HubSpotFormDialog: React.FC<HubSpotFormDialogProps> = ({
  config,
  campaignTypes,
  isLoadingCampaignTypes,
  isSaving,
  hasProductInterestProperty,
  onSave,
  onClose
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(config);
  // Product lists are typed as text; null keeps a rule's saved list untouched.
  const [productTexts, setProductTexts] = useState<(string | null)[]>(
    config.ownerRules.map(() => null)
  );
  const [valueLabelRows, setValueLabelRows] = useState<[string, string][]>(
    Object.entries(config.productInterest.valueLabels)
  );

  const origin = draft.productInterest.origin;
  // Inputs keep what was typed so spaces between words survive; padding is
  // trimmed once here.
  const result: HubSpotFormConfig = {
    ...draft,
    productInterest: {
      ...draft.productInterest,
      property: blankToNull(draft.productInterest.property),
      value: blankToNull(draft.productInterest.value),
      valueLabels: origin === "field" ? valueLabelsFromRows(valueLabelRows) : {}
    },
    ownerRules: draft.ownerRules.map((rule, index) => {
      const text = productTexts[index];
      return text === null ? rule : { ...rule, products: parseProducts(text) };
    }),
    noteProperties: draft.noteProperties.map((note) => ({
      ...note,
      property: note.property.trim(),
      label: note.label.trim()
    }))
  };
  const errors = [
    ...formConfigErrors(result, valueLabelRows),
    ...(origin !== "none" && !hasProductInterestProperty
      ? (["productInterestPropertyMissing"] as const)
      : [])
  ];

  const updateProductInterest = (
    changes: Partial<HubSpotFormConfig["productInterest"]>
  ) =>
    setDraft((current) => ({
      ...current,
      productInterest: { ...current.productInterest, ...changes }
    }));

  const updateOwner = (
    index: number,
    role: keyof LeadOwnerEmails,
    email: string | null
  ) =>
    setDraft((current) => ({
      ...current,
      ownerRules: current.ownerRules.map((rule, ruleIndex) =>
        ruleIndex === index
          ? { ...rule, owners: { ...rule.owners, [role]: email } }
          : rule
      )
    }));

  const moveRule = (index: number, offset: -1 | 1) => {
    const swap = <T,>(items: T[]) => {
      const next = [...items];
      [next[index], next[index + offset]] = [next[index + offset], next[index]];
      return next;
    };
    setDraft((current) => ({
      ...current,
      ownerRules: swap(current.ownerRules)
    }));
    setProductTexts(swap);
  };

  const removeRule = (index: number) => {
    setDraft((current) => ({
      ...current,
      ownerRules: current.ownerRules.filter((_, i) => i !== index)
    }));
    setProductTexts((texts) => texts.filter((_, i) => i !== index));
  };

  const addRule = () => {
    setDraft((current) => ({
      ...current,
      ownerRules: [
        ...current.ownerRules,
        { products: [], owners: { sdrOwner: null, aeOwner: null } }
      ]
    }));
    setProductTexts((texts) => [...texts, ""]);
  };

  const updateNote = (
    index: number,
    changes: Partial<HubSpotFormConfig["noteProperties"][number]>
  ) =>
    setDraft((current) => ({
      ...current,
      noteProperties: current.noteProperties.map((note, noteIndex) =>
        noteIndex === index ? { ...note, ...changes } : note
      )
    }));

  return (
    <Modal
      open
      setIsOpen={(open) => {
        if (!open && !isSaving) onClose();
      }}
      contentClassname="w-[45rem]"
      contentContainerClassname="max-h-[70vh] overflow-y-auto"
      data-test="hubspot-form-dialog"
      title={
        <Text as="div" variant="lgSubtitleH1">
          {config.formName}
        </Text>
      }
      content={
        <div className="flex flex-col gap-6 px-6 py-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label={t(`${PREFIX}.source.title`)}>
              <SingleSelect
                fieldName="source"
                options={LEAD_SOURCES.map((source) => ({
                  id: source,
                  displayName: t(`${PREFIX}.source.options.${source}`)
                }))}
                selectedValue={draft.source}
                onSelection={(value) =>
                  setDraft((current) => {
                    const source = Number(value) as LeadType;
                    // The campaign type is hidden and unused for any other source.
                    return source === current.source
                      ? current
                      : {
                          ...current,
                          source,
                          inboundCampaignType:
                            source === LeadType.INBOUND
                              ? current.inboundCampaignType
                              : null
                        };
                  })
                }
                formatter={(value) => t(`${PREFIX}.source.options.${value}`)}
                disabled={isSaving}
                className={SELECT_CLASSNAME}
                popoverTriggerClassName={SELECT_TRIGGER_CLASSNAME}
                variant="form"
              />
            </Field>
            {draft.source === LeadType.INBOUND && (
              <Field label={t(`${PREFIX}.campaignType.title`)}>
                <SingleSelect
                  fieldName="inboundCampaignType"
                  options={getCampaignTypeOptions(campaignTypes)}
                  selectedValue={draft.inboundCampaignType ?? undefined}
                  onSelection={(value) =>
                    setDraft((current) => ({
                      ...current,
                      inboundCampaignType: Number(value)
                    }))
                  }
                  formatter={(value) =>
                    campaignTypes.find((type) => type.id === Number(value))
                      ?.name ?? String(value)
                  }
                  placeholder={t(`${PREFIX}.campaignType.placeholder`)}
                  clearable
                  onClear={() =>
                    setDraft((current) => ({
                      ...current,
                      inboundCampaignType: null
                    }))
                  }
                  disabled={isSaving}
                  isLoading={isLoadingCampaignTypes}
                  className={SELECT_CLASSNAME}
                  popoverTriggerClassName={SELECT_TRIGGER_CLASSNAME}
                  variant="form"
                />
              </Field>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <Text variant="lgLabel">
              {t(`${PREFIX}.productInterest.title`)}
            </Text>
            <Text variant="bodySecondary">
              {t(`${PREFIX}.productInterest.subtitle`)}
            </Text>
            <div className="grid grid-cols-2 gap-4">
              <Field label={t(`${PREFIX}.productInterest.origin.title`)}>
                <SingleSelect
                  fieldName="productInterestOrigin"
                  options={ORIGINS.map((option) => ({
                    id: option,
                    displayName: t(
                      `${PREFIX}.productInterest.origin.options.${option}`
                    )
                  }))}
                  selectedValue={origin}
                  onSelection={(value) =>
                    updateProductInterest({
                      origin: value as ProductInterestOrigin
                    })
                  }
                  formatter={(value) =>
                    t(`${PREFIX}.productInterest.origin.options.${value}`)
                  }
                  disabled={isSaving}
                  className={SELECT_CLASSNAME}
                  popoverTriggerClassName={SELECT_TRIGGER_CLASSNAME}
                  variant="form"
                />
              </Field>
              {origin === "field" && (
                <Field label={t(`${PREFIX}.productInterest.property`)}>
                  <Input
                    aria-label={t(`${PREFIX}.productInterest.property`)}
                    maxLength={MAX_NAME_LENGTH}
                    value={draft.productInterest.property ?? ""}
                    onChange={(event) =>
                      updateProductInterest({
                        property: blankToNull(event.target.value)
                          ? event.target.value
                          : null
                      })
                    }
                    hasError={errors.includes("propertyRequired")}
                    disabled={isSaving}
                  />
                </Field>
              )}
              {origin === "fixed" && (
                <Field label={t(`${PREFIX}.productInterest.value`)}>
                  <Input
                    aria-label={t(`${PREFIX}.productInterest.value`)}
                    maxLength={MAX_NAME_LENGTH}
                    value={draft.productInterest.value ?? ""}
                    onChange={(event) =>
                      updateProductInterest({
                        value: blankToNull(event.target.value)
                          ? event.target.value
                          : null
                      })
                    }
                    hasError={errors.includes("valueRequired")}
                    disabled={isSaving}
                  />
                </Field>
              )}
            </div>
            {origin === "field" && (
              <div className="flex flex-col gap-2">
                <Text variant="bodySecondary">
                  {t(`${PREFIX}.productInterest.valueLabels.subtitle`)}
                </Text>
                {valueLabelRows.map(([key, label], index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      aria-label={t(`${PREFIX}.a11y.valueLabelKey`, {
                        position: index + 1
                      })}
                      placeholder={t(
                        `${PREFIX}.productInterest.valueLabels.value`
                      )}
                      maxLength={MAX_NAME_LENGTH}
                      value={key}
                      onChange={(event) =>
                        setValueLabelRows((rows) =>
                          rows.map((row, i) =>
                            i === index ? [event.target.value, row[1]] : row
                          )
                        )
                      }
                      disabled={isSaving}
                      containerClassName="flex-1"
                    />
                    <Input
                      aria-label={t(`${PREFIX}.a11y.valueLabelLabel`, {
                        position: index + 1
                      })}
                      placeholder={t(
                        `${PREFIX}.productInterest.valueLabels.label`
                      )}
                      maxLength={MAX_NAME_LENGTH}
                      value={label}
                      onChange={(event) =>
                        setValueLabelRows((rows) =>
                          rows.map((row, i) =>
                            i === index ? [row[0], event.target.value] : row
                          )
                        )
                      }
                      disabled={isSaving}
                      containerClassName="flex-1"
                    />
                    <IconButton
                      variant="iconNoBg"
                      size="lg"
                      aria-label={t(`${PREFIX}.a11y.removeValueLabel`, {
                        position: index + 1
                      })}
                      onClick={() =>
                        setValueLabelRows((rows) =>
                          rows.filter((_, i) => i !== index)
                        )
                      }
                      disabled={isSaving}
                    >
                      <Trash2 />
                    </IconButton>
                  </div>
                ))}
                <Button
                  variant="tertiary"
                  className="w-fit"
                  onClick={() =>
                    setValueLabelRows((rows) => [...rows, ["", ""]])
                  }
                  disabled={
                    isSaving || valueLabelRows.length >= MAX_VALUE_LABELS
                  }
                >
                  {t(`${PREFIX}.productInterest.valueLabels.add`)}
                </Button>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <Text variant="lgLabel">{t(`${PREFIX}.owners.title`)}</Text>
            <Text variant="bodySecondary">
              {t(`${PREFIX}.owners.subtitle`)}
            </Text>
            {draft.ownerRules.map((rule, index) => (
              <div
                key={index}
                className="flex flex-col gap-2 rounded-lg border border-border-border1 p-3"
                data-test={`hubspot-owner-rule-${index}`}
              >
                <div className="flex items-end gap-2">
                  <Field label={t(`${PREFIX}.owners.products`)}>
                    <Input
                      aria-label={t(`${PREFIX}.a11y.ruleProducts`, {
                        position: index + 1
                      })}
                      placeholder={t(`${PREFIX}.owners.productsPlaceholder`)}
                      value={
                        productTexts[index] ?? formatProducts(rule.products)
                      }
                      onChange={(event) =>
                        setProductTexts((texts) =>
                          texts.map((text, i) =>
                            i === index ? event.target.value : text
                          )
                        )
                      }
                      disabled={isSaving}
                      containerClassName="w-[26rem]"
                    />
                  </Field>
                  <IconButton
                    variant="iconNoBg"
                    size="lg"
                    aria-label={t(`${PREFIX}.a11y.moveRuleUp`, {
                      position: index + 1
                    })}
                    onClick={() => moveRule(index, -1)}
                    disabled={isSaving || index === 0}
                  >
                    <ArrowUp />
                  </IconButton>
                  <IconButton
                    variant="iconNoBg"
                    size="lg"
                    aria-label={t(`${PREFIX}.a11y.moveRuleDown`, {
                      position: index + 1
                    })}
                    onClick={() => moveRule(index, 1)}
                    disabled={isSaving || index === draft.ownerRules.length - 1}
                  >
                    <ArrowDown />
                  </IconButton>
                  <IconButton
                    variant="iconNoBg"
                    size="lg"
                    aria-label={t(`${PREFIX}.a11y.removeRule`, {
                      position: index + 1
                    })}
                    onClick={() => removeRule(index)}
                    disabled={isSaving}
                  >
                    <Trash2 />
                  </IconButton>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {OWNER_ROLES.map((role) => (
                    <Field key={role} label={t(`${PREFIX}.owners.${role}`)}>
                      <OwnerSelect
                        fieldName={`${role}-${index}`}
                        email={rule.owners[role]}
                        onChange={(email) => updateOwner(index, role, email)}
                        placeholder={t(`${PREFIX}.owners.unassigned`)}
                        unknownUserLabel={t(`${PREFIX}.owners.unknownUser`)}
                        disabled={isSaving}
                        className={SELECT_CLASSNAME}
                        popoverTriggerClassName={SELECT_TRIGGER_CLASSNAME}
                      />
                    </Field>
                  ))}
                </div>
              </div>
            ))}
            <Button
              variant="tertiary"
              className="w-fit"
              onClick={addRule}
              disabled={isSaving || draft.ownerRules.length >= MAX_OWNER_RULES}
            >
              {t(`${PREFIX}.owners.add`)}
            </Button>
          </div>

          <div className="flex flex-col gap-3">
            <Text variant="lgLabel">{t(`${PREFIX}.notes.title`)}</Text>
            <Text variant="bodySecondary">{t(`${PREFIX}.notes.subtitle`)}</Text>
            {draft.noteProperties.map((note, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  aria-label={t(`${PREFIX}.a11y.noteProperty`, {
                    position: index + 1
                  })}
                  placeholder={t(`${PREFIX}.notes.property`)}
                  maxLength={MAX_NAME_LENGTH}
                  value={note.property}
                  onChange={(event) =>
                    updateNote(index, { property: event.target.value })
                  }
                  disabled={isSaving}
                  containerClassName="flex-1"
                />
                <Input
                  aria-label={t(`${PREFIX}.a11y.noteLabel`, {
                    position: index + 1
                  })}
                  placeholder={t(`${PREFIX}.notes.label`)}
                  maxLength={MAX_NAME_LENGTH}
                  value={note.label}
                  onChange={(event) =>
                    updateNote(index, { label: event.target.value })
                  }
                  disabled={isSaving}
                  containerClassName="flex-1"
                />
                <IconButton
                  variant="iconNoBg"
                  size="lg"
                  aria-label={t(`${PREFIX}.a11y.removeNote`, {
                    position: index + 1
                  })}
                  onClick={() =>
                    setDraft((current) => ({
                      ...current,
                      noteProperties: current.noteProperties.filter(
                        (_, i) => i !== index
                      )
                    }))
                  }
                  disabled={isSaving}
                >
                  <Trash2 />
                </IconButton>
              </div>
            ))}
            <Button
              variant="tertiary"
              className="w-fit"
              onClick={() =>
                setDraft((current) => ({
                  ...current,
                  noteProperties: [
                    ...current.noteProperties,
                    { property: "", label: "" }
                  ]
                }))
              }
              disabled={
                isSaving || draft.noteProperties.length >= MAX_NOTE_PROPERTIES
              }
            >
              {t(`${PREFIX}.notes.add`)}
            </Button>
          </div>

          {errors.length > 0 && (
            <div
              className="flex flex-col gap-1"
              data-test="hubspot-form-errors"
            >
              {errors.map((error) => (
                <Text key={error} variant="smBodyError">
                  {t(`${PREFIX}.errors.${error}`)}
                </Text>
              ))}
            </div>
          )}
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            data-test="hubspot-form-save"
            onClick={() => onSave(result)}
            disabled={isSaving || errors.length > 0}
          >
            {t("save")}
          </Button>
        </div>
      }
    />
  );
};
